import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  SxProps,
  Theme,
  ListSubheader,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import type { Tenant as AppTenant, TenantMembership } from '../App';
import type { PageName } from '../types';
import DeleteTenantDialog, { TenantToDelete } from './DeleteTenant';

interface Tenant extends AppTenant {
  dataSourceCount?: number;
  allDataSourceCount?: number;
}

const TenantButtonIcon = ({ size = '26px' }: { size?: string }) => (
  <img
    src={process.env.PUBLIC_URL + '/icons/building-line.svg'}
    alt="Tenant Icon"
    style={{ width: size, height: size }}
  />
);

interface MenuItemConfig {
  name: string;
  iconPath: string;
  activeIconPath: string;
  path: PageName;
}

interface SidebarProps {
  open: boolean;
  drawerWidth: number;
  toggleSidebar: () => void;
  onNavigate: (page: PageName) => void;
  activePage: PageName;
  sx?: SxProps<Theme>;
  onAddNewTenant: () => void;
  currentTenant: Tenant | null;
  allTenants: Tenant[];
  isLoadingTenants: boolean;
  onChangeTenant: (tenantId: string) => void;
  onTenantDeleted: (tenantId: string) => void;
  userRole?: string | null;
  tenantMemberships?: TenantMembership[] | null;
}

const menuItems: MenuItemConfig[] = [
  { name: 'Dashboard', iconPath: '/icons/Vector.svg', activeIconPath: '/icons/VectorBlue.svg', path: 'dashboard' },
  { name: 'Cost Analysis', iconPath: '/icons/CostAnalysis.svg', activeIconPath: '/icons/cost-analysis-blue.svg', path: 'cost-analysis' },
  { name: 'Kubernetes Analysis', iconPath: '/icons/k8s.svg', activeIconPath: '/icons/k8s-fill.svg', path: 'kubernetes-analysis' },
  { name: 'LLM Observability', iconPath: '/icons/llm-observability.svg', activeIconPath: '/icons/llm-observability-blue.svg', path: 'llm-observability' },
  { name: 'Copilot', iconPath: '/icons/Outlinedtools-line.svg', activeIconPath: '/icons/tools-fill.svg', path: 'copilot' },
  { name: 'Budget & Alerts', iconPath: '/icons/Budgets&Alerts.svg', activeIconPath: '/icons/budget-alerts-blue.svg', path: 'budget-alerts' },
  { name: 'Data Sources', iconPath: '/icons/DataSources.svg', activeIconPath: '/icons/data-sources-blue.svg', path: 'data-sources' },
  { name: 'User Management', iconPath: '/icons/UserManagement.svg', activeIconPath: '/icons/user-management-blue.svg', path: 'user-management' },
  // { name: 'Settings', iconPath: '/icons/Settings.svg', activeIconPath: '/icons/Settings-blue.svg', path: 'settings' },
  // { name: 'Copilot', iconPath: '/icons/Outlinedtools-line.svg', activeIconPath: '/icons/tools-fill.svg', path: 'copilot' },
  
];

const Sidebar: React.FC<SidebarProps> = ({
  open,
  drawerWidth: passedDrawerWidth,
  toggleSidebar,
  onNavigate,
  activePage,
  sx,
  onAddNewTenant,
  currentTenant,
  allTenants,
  isLoadingTenants,
  onChangeTenant,
  onTenantDeleted, // MODIFIED: Destructure new prop
  userRole,
  tenantMemberships,
}) => {
  const finOpsIconUrl = '/finops-icon.svg';
  const iconSize = 30;
  const menuItemIconSize = '13px';
  const squareBackgroundColor = '#0064FF';

  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const [tenantMenuAnchorEl, setTenantMenuAnchorEl] = useState<null | HTMLElement>(null);
  const tenantMenuOpen = Boolean(tenantMenuAnchorEl);
  const [hoveredTenantId, setHoveredTenantId] = useState<string | null>(null);
  const [isDeleteTenantDialogOpen, setDeleteTenantDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<TenantToDelete | null>(null);

  const handleTenantMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setTenantMenuAnchorEl(event.currentTarget);
  };

  const handleTenantMenuClose = () => {
    setTenantMenuAnchorEl(null);
  };

  const handleSelectTenantFromMenu = (tenantId: string) => {
    onChangeTenant(tenantId);
    handleTenantMenuClose();
  };

  const handleAddNewTenantFromMenu = () => {
    onAddNewTenant();
    handleTenantMenuClose();
  };

  const openDeleteTenantDialog = (tenant: TenantToDelete) => {
    setTenantToDelete(tenant);
    setDeleteTenantDialogOpen(true);
    handleTenantMenuClose();
  };

  const closeDeleteTenantDialog = () => {
    setTenantToDelete(null);
    setDeleteTenantDialogOpen(false);
  };

  // <<< THIS IS THE MODIFIED LOGIC >>>
  const visibleMenuItems = menuItems.filter(item => {
    // These pages require an active tenant to be selected.
    if (['cost-analysis', 'kubernetes-analysis', 'data-sources'].includes(item.path)) {
        return !!currentTenant; // Only show if a tenant is active
    }

    // This page requires the user to be an admin.
    if (item.path === 'user-management') {
      return userRole === 'admin';
    }

    // Show all other items by default (like Dashboard and Copilot).
    return true;
  });

const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 10px 10px 16px', height: '64px', flexShrink: 0 }}>
        {open && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                width: iconSize, height: iconSize, backgroundColor: squareBackgroundColor,
                borderRadius: '7px', display: 'flex', alignItems: 'center',
                justifyContent:'end', marginRight: '10px',
              }}
            >
              <img
                src={process.env.PUBLIC_URL + finOpsIconUrl}
                alt="FinOps Cloud Icon"
                style={{ width: '80%', height: '70%', marginRight: '-3px' }}
              />
            </Box>
            {/* <Typography
              sx={{
                color: '#0064FF', fontWeight: 800, fontFamily: 'Lexend Zetta', fontSize: '18px'
              }}
            >
            View
            </Typography> */}
            <Typography
                  sx={{
                    color: '#0064FF',
                    fontWeight: 1000,
                    fontFamily:'Gilroy-SemiBold',
                    // fontFamily: 'Arvo',
                    fontSize: '22px',
                    letterSpacing: '1px',
                    // lineHeight:'200px'
                  }}
                >
                 Horizon
              </Typography>
          </Box>
        )}
        {!open && isLargeScreen && (
            <Box
              onClick={toggleSidebar} // Make the Box itself clickable
              sx={{
                width: iconSize,
                height: iconSize,
                backgroundColor: squareBackgroundColor,
                borderRadius: '7px',
                display: 'flex',
                alignItems: 'center',
                justifyContent:'end',
                cursor: 'pointer', // Add a pointer cursor on hover
                // Position the icon correctly when the sidebar is collapsed
              }}
            >
              <img
                src={process.env.PUBLIC_URL + finOpsIconUrl}
                alt="FinOps Cloud Icon"
                style={{ width: '80%', height: '70%', marginRight: '-3px' }}
              />
            </Box>
        )}
      </Box>
      <Box sx={{ overflowY: 'auto', flexShrink: 1, overflowX: 'hidden' }}>
        <List sx={{ mt: open ? 0 : (!open && isLargeScreen ? 0.5 : 1), pt: 2 }}>
          {visibleMenuItems.map((item) => {
            const isSelected = activePage === item.path;
            const currentIconPath = isSelected ? item.activeIconPath : item.iconPath;
            return (
              <ListItem key={item.name} disablePadding sx={{ display: 'block' }}>
                <ListItemButton
                  onClick={() => onNavigate(item.path)}
                  selected={isSelected}
                  title={item.name}
                  disableRipple // The single, correct prop
                  sx={{
                    minHeight: 10,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2,
                    borderRadius: '10px',
                    margin: open ? '4px 8px' : '4px auto',
                    width: open ? 'auto' : '44px',
                    height: open ? 'auto' : '44px',
                    backgroundColor: isSelected ? '#F2F7FF' : 'transparent',
                    color: isSelected ? '#0064FF' : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: isSelected ? '#E6F0FF' : theme.palette.action.hover,
                    },
                    '&.Mui-selected': {
                      backgroundColor: '#F2F7FF',
                      color: '#0064FF', // Fixed: Added '#' prefix
                      '&:hover': {
                        backgroundColor: '#E6F0FF',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: open ? 1 : 'auto', justifyContent: 'center' }}>
                    <img
                      src={process.env.PUBLIC_URL + currentIconPath}
                      alt={`${item.name} icon`}
                      style={{ width: menuItemIconSize, height: menuItemIconSize }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    sx={{
                      opacity: open ? 1 : 0, color: 'inherit',
                      '& .MuiTypography-root': { fontSize:'14px',fontWeight: isSelected ? '600' : '500' }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box sx={{ flexGrow: 1, flexShrink: 0 }} />

      <Box sx={{ p: open ? 2 : 1.25, pt:1, pb: open ? 2: 1.25, flexShrink: 0, display: 'flex', justifyContent: open ? 'initial' : 'center' }}>
        {isLoadingTenants ? (
          <Button fullWidth={open} variant="outlined" disabled sx={{ height: '44px', justifyContent: 'center' }}>
            {open ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <CircularProgress size={24} />}
            {open && "Loading..."}
          </Button>
        ) : (!currentTenant && allTenants.length === 0) ? (
            <Button
                  variant="outlined"
                  fullWidth={open}
                  startIcon={open ? <TenantButtonIcon /> : null}
                  onClick={onAddNewTenant}
                  sx={{
                    fontFamily: 'Inter',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '13px',
                    borderRadius: '12px',
                    height: '44px',
                    width: open ? '100%' : '40px',
                    minWidth: open ? 'auto' : '40px',
                    justifyContent: 'center',
                    py: 1.25,
                    px: open ? 1.5 : 0,
                    backgroundColor: '#E9F2FF',
                    color: '#0064FF',
                    border: 'none', // Explicitly remove the border
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: '#E9F2FF', // Keep the hover background color
                      // borderColor has been removed from hover state
                    },
                  }}
                          >
            {open ? "Add New Tenant" : <TenantButtonIcon size="28px"/>}
          </Button>

        ) : (
          <>
            <Button
              id="tenant-selector-sidebar-button"
              aria-controls={tenantMenuOpen ? 'tenant-sidebar-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={tenantMenuOpen ? 'true' : undefined}
              onClick={handleTenantMenuClick}
              fullWidth={open}
              sx={{
                fontFamily: 'Inter',
                backgroundColor: ' rgba(242, 247, 255, 1)',
                color: theme.palette.text.primary,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                borderRadius: '8px',
                justifyContent: 'space-between',
                width: '100%',
                height: '45px',
                px: 1.5,
                py: 1,
                display: open ? 'flex' : 'none',
              }}
              startIcon={<img
                    src={process.env.PUBLIC_URL + '/icons/tenant-icon.svg'}
                    alt="Delete tag"
                    style={{ width: '35px', height: '35px' }}
                  />}
              endIcon={tenantMenuOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            >
              <Typography
                  variant="body2"
                  component="span"
                  sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'left',
                      flexGrow: 1,
                  }}
              >
                  {currentTenant ? currentTenant.name : "Select Tenant"}
              </Typography>
            </Button>
            {!open && isLargeScreen && (
                 <IconButton
                    onClick={handleTenantMenuClick}
                    aria-controls={tenantMenuOpen ? 'tenant-sidebar-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={tenantMenuOpen ? 'true' : undefined}
                    title={currentTenant ? currentTenant.name : "Select Tenant"}
                    sx={{
                        width: '40px',
                        height: '40px',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        padding: '6px',
                        backgroundColor: theme.palette.background.paper,
                        '&:hover': { backgroundColor: theme.palette.action.hover },
                    }}
                 >
                    <TenantButtonIcon size="22px"/>
                 </IconButton>
            )}
            <Menu
              id="tenant-sidebar-menu"
              anchorEl={tenantMenuAnchorEl}
              open={tenantMenuOpen}
              onClose={handleTenantMenuClose}
              MenuListProps={{ 'aria-labelledby': 'tenant-selector-sidebar-button' }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  maxHeight: 450,
                  width: 'auto',
                  minWidth: '250px',
                  marginTop: theme.spacing(-6),
                  borderRadius: '8px',
                  boxShadow: '0px 4px 12px rgba(0,0,0,0.15)',
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
            >

              <Box sx={{ flexShrink: 1, height:'50px', padding:'2px'}}>
                <ListSubheader sx={{ fontFamily:'Inter',fontWeight: '600px', fontSize:'14px', color: ' #666666', pt: 2, pb: 1, lineHeight: '20px' }}>
                  Switch Tenant
                </ListSubheader>
               {/* <Divider sx={{marginTop:1.5}}/> */}
              </Box>

              <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                {allTenants.map((tenant) => {
                  const isHovered = hoveredTenantId === tenant.id;
                  const isSelected = tenant.id === currentTenant?.id;

                  const membership = tenantMemberships?.find(m => m.tenant?.id === tenant.id);
                  const roleForThisTenant = membership?.roleInTenant;

                  const handleDeleteClick = (e: React.MouseEvent) => {
                      e.stopPropagation();
                      openDeleteTenantDialog({
                        id: tenant.id,
                        name: tenant.name,
                        allDataSourceCount: tenant.allDataSourceCount,
                      });
                  };

                  return (
                    <MenuItem
                      key={tenant.id}
                      selected={isSelected}
                      onClick={() => handleSelectTenantFromMenu(tenant.id)}
                      onMouseEnter={() => setHoveredTenantId(tenant.id)}
                      onMouseLeave={() => setHoveredTenantId(null)}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt:1.5, pb:1.5, pl:2, pr:2 }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden', pr: 1 }}>
                        <Typography sx={{ fontSize:'14px', fontWeight: isSelected ? 400 : 400, color:' #333333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {tenant.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.25,fontSize:'12px', }}>
                          {typeof tenant.allDataSourceCount === 'number' ? tenant.allDataSourceCount : '0'} Data sources
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: '56px' }}>
                            {isSelected && (
                              <Box
                                component="img"
                                src="/icons/check-icon.svg"
                                alt="Selected"
                                sx={{
                                  width: '14px',
                                  height: '14px',
                                }}
                              />
                            )}
                            {roleForThisTenant === 'admin' && isHovered && (
                              <IconButton
                                size="small"
                                title={`Delete ${tenant.name}`}
                                onClick={handleDeleteClick}
                                sx={{ ml: 1 }}
                              >
                                <Box
                                  component="img"
                                  src="/icons/delete_icon.svg"
                                  alt={`Delete ${tenant.name}`}
                                  sx={{
                                    width: '14px',
                                    height: '14px',
                                  }}
                                />
                              </IconButton>
                            )}
                          </Box>
                    </MenuItem>
                  )
                })}
              </Box>

              <Box sx={{ flexShrink: 0, borderTop: `1px solid ${theme.palette.divider}` }}>
                <MenuItem onClick={handleAddNewTenantFromMenu} sx={{ pt:1.5, pb:1.5, pl:4, pr:1}}>
                  <ListItemIcon sx={{minWidth: 'auto',ml:3 }}>
                      <TenantButtonIcon size="18px"/>
                      <ListItemText primary="Add New Tenant" primaryTypographyProps={{ marginLeft:2,color: "#0064FF", fontWeight:500, fontSize:'14px'}}/>
                  </ListItemIcon>
                </MenuItem>
              </Box>
            </Menu>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Drawer
        variant={isLargeScreen ? "permanent" : "temporary"}
        open={open}
        onClose={!isLargeScreen ? toggleSidebar : undefined}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: passedDrawerWidth, flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: passedDrawerWidth, boxSizing: 'border-box',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
            backgroundColor: 'background.paper',
            borderRight: `0.7px solid ${
        theme.palette.mode === 'light'
          ? theme.palette.grey[200]
          : theme.palette.grey[800]
      }`,
          },
          ...sx
        }}
      >
        {drawerContent}
      </Drawer>
      <DeleteTenantDialog
        open={isDeleteTenantDialogOpen}
        onClose={closeDeleteTenantDialog}
        onTenantDeleted={onTenantDeleted}
        tenantToDelete={tenantToDelete}
      />
    </>
  );
};

export default Sidebar;